import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Checkbox, FormControlLabel, LinearProgress, Dialog, DialogTitle, DialogContent, TextField, DialogActions, MenuItem, Select, InputLabel, FormControl, Autocomplete } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { okrApi } from '../services/okrApi';
import { IndividualObjective, TeamObjective } from '../types';
import { useAuth } from '@store/authStore';
import httpClient from '@utils/httpClient';
import OKRCard from '../components/OKRCard';

const OKRMyView: React.FC = () => {
    const { user } = useAuth();
    const [objectives, setObjectives] = useState<IndividualObjective[]>([]);
    const [teamObjectives, setTeamObjectives] = useState<TeamObjective[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    // Create Objective
    const [openCreate, setOpenCreate] = useState(false);
    const [newObjTitle, setNewObjTitle] = useState('');
    const [selectedTeamObj, setSelectedTeamObj] = useState('');

    // Weekly Update
    const [openUpdate, setOpenUpdate] = useState(false);
    const [selectedObjForUpdate, setSelectedObjForUpdate] = useState<IndividualObjective | null>(null);
    const [updateStatus, setUpdateStatus] = useState('not_started');
    const [updateComment, setUpdateComment] = useState('');
    const [updateBlockers, setUpdateBlockers] = useState('');

    // Milestones
    const [openCreateMilestone, setOpenCreateMilestone] = useState(false);
    const [selectedObjForMilestone, setSelectedObjForMilestone] = useState<IndividualObjective | null>(null);
    const [milestoneTitle, setMilestoneTitle] = useState('');
    const [milestoneDueDate, setMilestoneDueDate] = useState('');

    // Filtering
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    const isAdmin = user?.role === 'Admin';
    const isManager = user?.role === 'Manager';
    const isHR = user?.role === 'HR';
    const canFilter = isHR || isManager;

    const fetchData = async () => {
        try {
            setLoading(true);
            const [myData, teamData] = await Promise.all([
                okrApi.getMyOkrs(selectedUserId || undefined),
                okrApi.getTeamOkrs()
            ]);
            setObjectives(myData);
            setTeamObjectives(teamData);
        } catch (error) {
            console.error('Failed to fetch OKRs', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const response = await httpClient.get<{ success: boolean; users: any[] }>('/users');
            if (response.users) {
                let usersList = response.users;
                if (isManager && user?.id) {
                    const me = usersList.find((u: any) => u.id === user.id);
                    if (me && me.dept_id) {
                        usersList = usersList.filter((u: any) => u.dept_id === me.dept_id);
                    } else if (user.department_id) {
                        usersList = usersList.filter((u: any) => u.dept_id === user.department_id);
                    }
                }
                usersList = usersList.filter((u: any) => u.role_name !== 'Admin' && u.role_name !== 'Super Admin');
                setAllUsers(usersList);
            }
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    };

    useEffect(() => {
        if (canFilter) fetchAllUsers();
    }, [canFilter]);

    useEffect(() => {
        fetchData();
    }, [selectedUserId]);

    const toggleExpand = (id: string) => {
        const newSet = new Set(expandedIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setExpandedIds(newSet);
    };

    const handleCreateObjective = async () => {
        if (!selectedTeamObj || !newObjTitle) return;
        try {
            await okrApi.createIndividualObjective(selectedTeamObj, newObjTitle);
            setOpenCreate(false);
            setNewObjTitle('');
            setSelectedTeamObj('');
            fetchData();
        } catch (error) {
            console.error('Create failed', error);
        }
    };

    const handleMilestoneToggle = async (milestoneId: string, currentStatus: string) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await okrApi.updateMilestoneStatus(milestoneId, currentStatus === 'COMPLETED' ? 'PENDING' : 'COMPLETED');
            fetchData();
        } catch (error) {
            console.error('Update failed', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitUpdate = async () => {
        if (!selectedObjForUpdate) return;
        try {
            await okrApi.createWeeklyUpdate({
                objective_id: selectedObjForUpdate.id,
                status: updateStatus,
                comment: updateComment,
                blockers: updateBlockers
            });
            setOpenUpdate(false);
            setUpdateStatus('GREEN');
            setUpdateComment('');
            setUpdateBlockers('');
        } catch (error) { }
    };

    const handleCreateMilestone = async () => {
        if (!selectedObjForMilestone || !milestoneTitle || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await okrApi.createMilestone(selectedObjForMilestone.id, milestoneTitle, milestoneDueDate || undefined);
            setOpenCreateMilestone(false);
            setMilestoneTitle('');
            setMilestoneDueDate('');
            fetchData();
        } catch (error) {
            console.error('Create milestone failed', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#1A2027', mb: 0.5 }}>
                        My Objectives
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track your personal contribution to company goals.
                    </Typography>
                </Box>
                {(isAdmin || isManager) && (
                    <Button
                        variant="contained"
                        onClick={() => setOpenCreate(true)}
                        sx={{
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)'
                        }}
                    >
                        + New Objective
                    </Button>
                )}
            </Box>

            {canFilter && (
                <Box mb={4}>
                    <Autocomplete
                        options={allUsers}
                        getOptionLabel={(option) => `${option.name} (${option.email})`}
                        value={allUsers.find(u => u.id === selectedUserId) || null}
                        onChange={(event, newValue) => setSelectedUserId(newValue ? newValue.id : null)}
                        renderInput={(params) => <TextField {...params} label="Filter by Employee" size="small" />}
                    />
                </Box>
            )}

            <Box display="flex" flexDirection="column" gap={0}>
                {objectives.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 4, border: '1px dashed #ccc' }}>
                        <Typography color="text.secondary" gutterBottom>No objectives found.</Typography>
                        {(isAdmin || isManager) && (
                            <Button size="small" onClick={() => setOpenCreate(true)}>Create your first OKR</Button>
                        )}
                    </Box>
                ) : (
                    objectives.map((obj) => (
                        <OKRCard
                            key={obj.id}
                            title={obj.title}
                            description={`Contribution to: ${obj.team_context || 'Team Objective'}`}
                            score={obj.score}
                            type="INDIVIDUAL"
                            isExpanded={expandedIds.has(obj.id)}
                            onToggleExpand={() => toggleExpand(obj.id)}
                            onCheckIn={() => { setSelectedObjForUpdate(obj); setOpenUpdate(true); }}
                        >
                            <Box sx={{ p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom color="text.secondary" fontWeight={600}>Milestones & Key Results</Typography>
                                {obj.milestones?.map((m) => (
                                    <FormControlLabel
                                        key={m.id}
                                        control={
                                            <Checkbox
                                                checked={m.status === 'COMPLETED'}
                                                onChange={() => handleMilestoneToggle(m.id, m.status)}
                                                icon={<RadioButtonUnchecked />}
                                                checkedIcon={<CheckCircle sx={{ color: '#4caf50' }} />}
                                                disabled={isSubmitting}
                                            />
                                        }
                                        label={
                                            <Typography sx={{
                                                textDecoration: m.status === 'COMPLETED' ? 'line-through' : 'none',
                                                color: m.status === 'COMPLETED' ? 'text.disabled' : 'text.primary',
                                                fontSize: '0.95rem'
                                            }}>
                                                {m.title}
                                            </Typography>
                                        }
                                        sx={{ display: 'block', mb: 1, ml: 0 }}
                                    />
                                ))}
                                {(!obj.milestones || obj.milestones.length === 0) && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                                        No milestones defined. Break it down!
                                    </Typography>
                                )}
                                <Button
                                    size="small"
                                    onClick={() => { setSelectedObjForMilestone(obj); setOpenCreateMilestone(true); }}
                                    sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                                >
                                    + Add Milestone
                                </Button>
                            </Box>
                        </OKRCard>
                    ))
                )}
            </Box>

            {/* Dialogs */}
            <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
                <DialogTitle>Create Individual Objective</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        All individual objectives must align with a Team Objective.
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Select Team Objective</InputLabel>
                        <Select value={selectedTeamObj} label="Select Team Objective" onChange={(e) => setSelectedTeamObj(e.target.value)}>
                            {teamObjectives.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField label="Objective Title" fullWidth value={newObjTitle} onChange={(e) => setNewObjTitle(e.target.value)} />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
                    <Button onClick={handleCreateObjective} variant="contained">Create</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openUpdate} onClose={() => setOpenUpdate(false)} fullWidth maxWidth="sm">
                <DialogTitle>Weekly Check-in</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={updateStatus} label="Status" onChange={(e) => setUpdateStatus(e.target.value)}>
                            <MenuItem value="not_started">Not Started (0%)</MenuItem>
                            <MenuItem value="less_than_25">Less than 25%</MenuItem>
                            <MenuItem value="less_than_50">Less than 50%</MenuItem>
                            <MenuItem value="almost_75">Almost 75%</MenuItem>
                            <MenuItem value="almost_100">Almost 100%</MenuItem>
                            <MenuItem value="completed">Completed (100%)</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Comment" fullWidth multiline rows={3} value={updateComment} onChange={(e) => setUpdateComment(e.target.value)} sx={{ mb: 2 }} />
                    <TextField label="Blockers" fullWidth value={updateBlockers} onChange={(e) => setUpdateBlockers(e.target.value)} />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenUpdate(false)}>Cancel</Button>
                    <Button onClick={handleSubmitUpdate} variant="contained">Submit</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openCreateMilestone} onClose={() => setOpenCreateMilestone(false)} fullWidth maxWidth="xs">
                <DialogTitle>Add Milestone</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" label="Title" fullWidth value={milestoneTitle} onChange={(e) => setMilestoneTitle(e.target.value)} sx={{ mb: 2 }} />
                    <TextField label="Due Date" type="date" fullWidth value={milestoneDueDate} onChange={(e) => setMilestoneDueDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenCreateMilestone(false)}>Cancel</Button>
                    <Button onClick={handleCreateMilestone} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OKRMyView;
